import { useContext, createContext, useState, useMemo } from 'react'

import {
  type AlertFamilyType,
  type CardsInfosSectionsType,
  type ApiDataType,
  type FamilyContextType,
  type FamilyMemberType,
} from 'types/services/Family'
import { type InconsistenciesType } from 'types/services/Inconsistencies'
import { handleData } from 'utils/handleData'
import { handleCardInfosSections } from 'utils/handleRecomendations'

import { 
  getAlertsTheBase,
  calculateNumberAlertsPerSections,
  getCheckInfosKinship,
  getBasesQuerySort
 } from 'utils/familyUtils'

type InconsistenciesDataEndpointType = null | undefined | InconsistenciesType
interface FamilyContextData {
  families: FamilyContextType[]
  setFamilies: (value: FamilyContextType[]) => void
  cardInfoSections: CardsInfosSectionsType[]
  handleWithFamiliesData: (
    value: ApiDataType[],
    basesQuery: string[],
    params: {
      cpf: string
      nis: string
      familyCode: string
      bases: Array<{
        id: string
        name: string
      }>
    }
  ) => void

  numberAlertsPerSections: NumberAlertsPerSectionsType
  alertsPerBases: AlertFamilyType

  inconsistencies: InconsistenciesDataEndpointType
  handleWithInconsistencies: (value: InconsistenciesDataEndpointType) => void
  setInfosKinship: (value: FamilyMemberType) => void
  resetStates: () => void
  infosKinship: FamilyMemberType
  bases: Array<{ id: string; name: string }>
  setBases: (value: Array<{ id: string; name: string }>) => void
  basesWithoutData: string[]
  basesWithTimeout: string[]
}

export const FamilyContext = createContext<FamilyContextData>(
  {} as FamilyContextData
)

interface ContextProps {
  children: React.ReactNode
}

export interface NumberAlertsPerSectionsType {
  income: number
  education: number
  residence: number
  health: number
  pregnancy: number
  nutrition: number
}

const FamilyProvider = ({ children }: ContextProps) => {
  const [families, setFamilies] = useState<FamilyContextType[]>(
    [] as FamilyContextType[]
  )

  const [bases, setBases] = useState<Array<{ id: string; name: string }>>([])
  const [cardInfoSections, setCardInfoSections] = useState<
    CardsInfosSectionsType[]
  >([])
  const [numberAlertsPerSections, setNumberAlertsPerSections] =
    useState<NumberAlertsPerSectionsType>({} as NumberAlertsPerSectionsType)
  const [alertsPerBases, setAlertsPerBases] = useState<AlertFamilyType>({})
  const [inconsistencies, setInconsistencies] = useState<
    InconsistenciesType | undefined | null
  >({} as InconsistenciesType)

  const [infosKinship, setInfosKinship] = useState<FamilyMemberType>(
    {} as FamilyMemberType
  )

  const [basesWithoutData, setBasesWithoutData] = useState<string[]>([])
  const [basesWithTimeout, setBasesWithTimeout] = useState<string[]>([])

  function resetStates() {
    setFamilies([])
    setCardInfoSections([])
    setNumberAlertsPerSections({} as NumberAlertsPerSectionsType)
    setAlertsPerBases({})
    setInconsistencies({} as InconsistenciesType)
  }

  function handleWithInconsistencies(data: InconsistenciesDataEndpointType) {
    setInconsistencies(data)
  }

  /* 
  🔵 Faz um tratamento nos dados enviados pelo back, gerando 
     informações úteis para o front. 
  */
  function handleWithFamiliesData(
    data: ApiDataType[],
    basesQuery: string[],
    params: {
      cpf: string
      nis: string
      familyCode: string
      bases: Array<{
        id: string
        name: string
      }>
    }
  ) {
    const newDataFamilies = handleData(data)

    const newInfosKinship = getCheckInfosKinship(data, params.cpf)

    setInfosKinship(newInfosKinship)

    const basesQuerySort = getBasesQuerySort(basesQuery, data)

    const familiesFormattedForBases: FamilyContextType[] = basesQuerySort.map(
      (base: string) => {
        return newDataFamilies.filter(
          (family: FamilyContextType) => family.base === base
        )[0]
      }
    )

    setFamilies(familiesFormattedForBases)

    const basesWithoutDataAux: string[] = []
    const basesWithTimeout: string[] = []

    familiesFormattedForBases.forEach((family: FamilyContextType) => {
      if (family?.family === undefined) basesWithoutDataAux.push(family?.base)
      if (family.hasTimeout) basesWithTimeout.push(family?.base)
    })

    setBasesWithoutData(basesWithoutDataAux)
    setBasesWithTimeout(basesWithTimeout)

    const dataCardsInfosSections: CardsInfosSectionsType[] =
      handleCardInfosSections(familiesFormattedForBases, inconsistencies)

    setCardInfoSections(dataCardsInfosSections)

    setNumberAlertsPerSections(
      calculateNumberAlertsPerSections(dataCardsInfosSections)
    )

    const alertsPerBasesAux: AlertFamilyType = {}
    basesQuery.forEach((base) => {
      alertsPerBasesAux[`${base}`] = getAlertsTheBase(
        dataCardsInfosSections,
        base
      )
    })

    setAlertsPerBases(alertsPerBasesAux)
  }

  const valuesProvider = useMemo(() => {
    return {
      families,
      cardInfoSections,
      setFamilies,
      handleWithFamiliesData,
      numberAlertsPerSections,
      alertsPerBases,
      inconsistencies,
      handleWithInconsistencies,
      setInfosKinship,
      infosKinship,
      resetStates,
      bases,
      setBases,
      basesWithoutData,
      basesWithTimeout,
    }
  }, [
    families,
    cardInfoSections,
    setFamilies,
    handleWithFamiliesData,
    numberAlertsPerSections,
    alertsPerBases,
    inconsistencies,
    handleWithInconsistencies,
    setInfosKinship,
    infosKinship,
    resetStates,
    bases,
    setBases,
    basesWithoutData,
    basesWithTimeout,
  ])

  return (
    <FamilyContext.Provider value={valuesProvider}>
      {children}
    </FamilyContext.Provider>
  )
}

const useFamily = () => useContext(FamilyContext)

export { FamilyProvider, useFamily }
